import { KEYWORD_GLOSS, KEYWORD_LABELS, KEYWORD_VALUES, type MentionRow } from './generator';

/**
 * Keyword search for `Keyword mentions & frequency`: what a query matches, and
 * which other keywords are related to those matches.
 *
 * In the data layer rather than the card because both halves are claims about
 * the corpus, and a claim about the corpus should be testable without React.
 */

/** How one keyword stands against the current query. */
export type KeywordRelation = 'match' | 'related';

export type KeywordHit = {
  keyword: string;
  mentions: number;
  relation: KeywordRelation;
};

/**
 * The share of a topic's mentions a keyword must carry to count as related.
 *
 * A keyword is related to a match when people talking about the same topics
 * use it too: for each topic, weight the keyword's share of that topic's
 * mentions by how much of the match's conversation sits in that topic, then sum.
 * That is the probability a mention *like the matched ones* uses this word.
 *
 * 4%, set against the corpus: a keyword its topic names carries 5% of that
 * topic or more, and one only the generator's floor reaches carries about 0.2%
 * (see `KEYWORD_FLOOR`). So 4% admits the named neighbours and nothing that
 * turned up by chance. Measured, not derived — revisit if the floor moves.
 */
export const RELATED_SHARE = 0.04;

/** Lower-case and trim, so `  Net ` and `net` are the same query. */
function normalise(query: string): string {
  return query.trim().toLocaleLowerCase();
}

/**
 * Whether a keyword matches the query, in either script.
 *
 * Label and gloss both, because the roster is mostly Bangla and the reader may
 * type in either: `net` finds `নেটওয়ার্ক` through its gloss, `রিচ` finds
 * `রিচার্জ` through its label. Substring rather than prefix, so `internet`
 * also finds `slow internet`.
 */
export function keywordMatches(keyword: string, query: string): boolean {
  const q = normalise(query);
  if (q === '') return true;
  const label = (KEYWORD_LABELS[keyword] ?? keyword).toLocaleLowerCase();
  const gloss = (KEYWORD_GLOSS[keyword] ?? '').toLocaleLowerCase();
  return label.includes(q) || gloss.includes(q);
}

/**
 * Every keyword in the rows, ranked for the query.
 *
 * - **No query:** every keyword with a mention, by volume, all `match`.
 * - **A query:** the matches by volume first, then the related keywords by
 *   volume. Matches lead because they're what the reader typed. A related word
 *   can be larger than the match that brought it in, and putting it on top
 *   would read as "the search found this", which it didn't.
 *
 * Relatedness is read off the **rows**, not the generator's tables, so it
 * follows the filters: narrow the chips to complaints and `নেটওয়ার্ক`'s
 * neighbours shift toward `কল ড্রপ` and `অভিযোগ`, because that's what the
 * remaining conversation contains.
 *
 * A query that matches nothing returns nothing. Related needs something to be
 * related to.
 */
export function searchKeywords(rows: readonly MentionRow[], query: string): KeywordHit[] {
  const mentions = new Map<string, number>();
  for (const row of rows) mentions.set(row.keyword, (mentions.get(row.keyword) ?? 0) + 1);

  const byVolume = (a: string, b: string) =>
    (mentions.get(b) ?? 0) - (mentions.get(a) ?? 0) ||
    KEYWORD_VALUES.indexOf(a as never) - KEYWORD_VALUES.indexOf(b as never);

  const present = [...mentions.keys()];
  const matched = present.filter((keyword) => keywordMatches(keyword, query)).sort(byVolume);

  const hit = (relation: KeywordRelation) => (keyword: string): KeywordHit => ({
    keyword,
    mentions: mentions.get(keyword) ?? 0,
    relation,
  });

  if (normalise(query) === '' || matched.length === 0) return matched.map(hit('match'));

  /* p(topic | the matches) — where the matched conversation sits. */
  const matchedSet = new Set(matched);
  const topicOfMatch = new Map<string, number>();
  let matchedTotal = 0;
  /* p(keyword | topic) needs each topic's total and each keyword's count in it. */
  const topicTotal = new Map<string, number>();
  const inTopic = new Map<string, Map<string, number>>();

  for (const row of rows) {
    topicTotal.set(row.topic, (topicTotal.get(row.topic) ?? 0) + 1);
    let counts = inTopic.get(row.topic);
    if (!counts) inTopic.set(row.topic, (counts = new Map()));
    counts.set(row.keyword, (counts.get(row.keyword) ?? 0) + 1);

    if (matchedSet.has(row.keyword)) {
      topicOfMatch.set(row.topic, (topicOfMatch.get(row.topic) ?? 0) + 1);
      matchedTotal += 1;
    }
  }

  const related = present
    .filter((keyword) => !matchedSet.has(keyword))
    .filter((keyword) => {
      let score = 0;
      for (const [topic, count] of topicOfMatch) {
        const total = topicTotal.get(topic) ?? 0;
        if (total === 0) continue;
        score += (count / matchedTotal) * ((inTopic.get(topic)?.get(keyword) ?? 0) / total);
      }
      return score >= RELATED_SHARE;
    })
    .sort(byVolume);

  return [...matched.map(hit('match')), ...related.map(hit('related'))];
}
